<template>
  <div>
    <v-row>
      <v-col cols="12" md="2">
        <v-card>
          <v-list shaped>
            <v-subheader>Invoice Sections</v-subheader>
            <v-list-item-group color="primary" v-model="currentIndex">
              <v-list-item v-for="(section, i) in formSections" :key="i" @click="viewSection(i)">
                <v-list-item-title v-text="section[1].name"></v-list-item-title>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="12" md="10">
        <v-card>
          <v-form>
            <v-card-text>
              <v-container>
                <draggable v-model="formSections[currentIndex][1].fields" @sort="handleSort">
                  <transition-group>
                    <v-row v-for="field in formSections[currentIndex][1].fields" :key="field.uniqId">
                      <v-icon>mdi-drag-horizontal-variant</v-icon>
                      <v-col cols="auto">
                        <v-checkbox v-model="field.required" label="Required"></v-checkbox>
                      </v-col>
                      <v-col cols="2">
                        <v-select
                          v-model="field.type"
                          :items="fieldTypes"
                          item-text="label"
                          item-value="value"
                          label="Type"
                          outlined
                          hide-details
                          return-object
                        ></v-select>
                      </v-col>
                      <v-col cols="3">
                        <v-text-field
                          v-model="field.label"
                          label="Field Name / Question"
                          hide-details
                        ></v-text-field>
                      </v-col>
                      <v-col cols="2">
                        <v-select
                          v-model="field.colspan"
                          :items="colspans"
                          item-text="label"
                          item-value="value"
                          label="Field Size"
                          outlined
                          hide-details
                        ></v-select>
                      </v-col>
                      <v-col cols="auto">
                        <v-btn
                          class="mx-2"
                          fab
                          color="primary"
                          @click="optionSettings(field)"
                          v-if="field.type?.hasOptions"
                        >
                          <v-icon>mdi-cog</v-icon>
                        </v-btn>
                      </v-col>
                      <v-col cols="auto">
                        <v-btn class="mx-2" fab dark color="error" @click="removeField(field)">
                          <v-icon dark>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </transition-group>
                </draggable>
              </v-container>
            </v-card-text>
          </v-form>
          <v-card-actions>
            <v-col cols="auto">
              <v-btn class="mx-2" dark color="primary" @click="addField()">
                <v-icon>mdi-plus</v-icon>Add Field
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn
                class="mx-2"
                dark
                color="success"
                @click="saveField()"
                v-show="formSections[currentIndex][1].fields && formSections[currentIndex][1].fields.length"
              >
                <v-icon>mdi-content-save</v-icon>Save
              </v-btn>
            </v-col>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="isAddingOptions" width="800">
      <v-card>
        <v-card-title>Custom Field Settings</v-card-title>
        <v-card-text>
          <v-subheader>Add list items (type in an option then press ENTER):</v-subheader>
          <v-row dense>
            <v-col cols="12">
              <v-combobox
                v-model="tempFieldMetadata.options"
                :items="[]"
                multiple
                chips
                @keydown.enter.prevent
              ></v-combobox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="isAddingOptions = false">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import configApi from '@/apis/config';
import promptBeforeLeave from '@/mixins/promptBeforeLeave';
import { randomString } from '@/util';
import { cloneDeep } from 'lodash';
import draggable from 'vuedraggable';
import { mapGetters } from 'vuex';

export default {
  name: 'InvoiceCustomFormFields',
  components: { draggable },
  data() {
    return {
      formSections: [],
      currentIndex: 0,
      baseFormValue: null,
      latestFormValue: null,
      isAddingOptions: false,
      dropdownFieldMetadata: {
        value: '',
        label: '',
        isValueEqualsLabel: true,
      },
      tempFieldMetadata: {},
      colspans: [
        { label: 'full-width', value: 12 },
        { label: '1/2', value: 6 },
        { label: '1/3', value: 4 },
        { label: '1/4', value: 3 },
      ],
    };
  },
  computed: {
    ...mapGetters('config', { fieldTypes: 'customFormFieldTypes' }),
  },
  methods: {
    async viewSection(i) {
      const prev = this.currentIndex;
      const yes = await this.performPromptBeforeLeave();

      if (!yes) {
        this.currentIndex = prev;
        return;
      }

      this.baseFormValue = cloneDeep(this.formSections[i]);
      this.latestFormValue = cloneDeep(this.formSections[i]);
      this.currentIndex = i;
    },
    addField() {
      const field = {
        uniqId: randomString(12),
        type: this.fieldTypes[0],
        label: '',
        colspan: 12,
        required: false,
        lengthValidation: {
          isPresent: false,
          min: null,
          max: null,
        },
        valueValidation: {
          isPresent: false,
          min: null,
          max: null,
        }
      };

      if (field.type.hasOptions) {
        field.options = [];
      } else {
        field.options = false;
      }

      this.formSections[this.currentIndex][1].fields.push(field);
    },
    async handleSort() {
      this.$emit('input', this.formSections[this.currentIndex][1]);
    },
    removeField(field) {
      this.formSections[this.currentIndex][1].fields = this.formSections[this.currentIndex][1].fields.filter(
        (f) => f.uniqId !== field.uniqId
      );
    },
    optionSettings(field) {
      this.tempFieldMetadata = field;
      if (!this.tempFieldMetadata.options) {
        this.tempFieldMetadata.options = [];
      }
      this.isAddingOptions = true;
    },
    addOption() {
      if (this.dropdownFieldMetadata.isValueEqualsLabel) {
        this.dropdownFieldMetadata.value = this.dropdownFieldMetadata.label;
      }
      
      this.tempFieldMetadata.options.push({ ...this.dropdownFieldMetadata });
      this.clearOptionsMetadata();
    },
    removeOption(field, option) {
      field.options.splice(field.options.indexOf(option), 1);
    },
    clearOptionsMetadata() {
      this.dropdownFieldMetadata = {
        value: '',
        label: '',
        isValueEqualsLabel: true,
      };
    },
    async saveField() {
      try {
        await configApi.saveCustomFormField(this.formSections[this.currentIndex][1]);
        this.$myalert.success('Custom fields saved successfully');
        this.baseFormValue = cloneDeep(this.formSections[this.currentIndex][1]);
      } catch (error) {
        this.$myalert.error(error?.message);
      }
    },
  },
  async mounted() {
    try {
      this.formSections = await configApi.getCustomFormFields('Invoice');
      this.baseFormValue = cloneDeep(this.formSections[this.currentIndex]);
    } catch (error) {
      this.$myalert.error(error?.message);
    }
  },
  watch: {
    formSections: {
      handler(value) {
        this.latestFormValue = cloneDeep(value[this.currentIndex]);
      },
      deep: true,
    },
  },
  mixins: [promptBeforeLeave],
};
</script>

<style scoped>
.table-action {
  margin-left: 10px;
}
.checkbox {
  display: flex;
  align-items: center !important;
}
</style>
